/**
 * SEN-Ed Teacher Questionnaire Framework
 * Purpose: Quickly identify observable needs without specialist knowledge.
 * Format: Tick-box / radio button style (2–6 options max per question).
 * Output: Strategies recommended automatically based on responses.
 * Time to complete: Under 10 minutes.
 */

/**
 * SEN-Ed Teacher Questionnaire Framework
 * Purpose: Quickly identify observable needs without specialist knowledge.
 * Format: Tick-box / radio button style (2–6 options max per question).
 * Output: Strategies recommended automatically based on responses.
 * Time to complete: Under 10 minutes.
 */

/** TRIAGE (4 attainment bands → 2 execution paths) */
export const TRIAGE_QUESTION = {
  id: 'triage-attainment-band',
  key: 'ncStatus', // keep key name for compatibility; value set to one of TriageChoice4
  label: 'Current attainment',
  type: 'single-select',
  required: true,
  options: [
    { value: 'age_expected',        label: 'The child is working at age-expected levels' },
    { value: 'one_to_two_below',    label: 'The child is working 1–2 years below' },
    { value: 'two_to_three_below',  label: 'The child is working 2–3 years below' },
    { value: 'four_or_more_below',  label: 'The child is working 4+ years below' },
  ],
  helpText: 'Select the best fit. This automatically sets the planning route.',
};

/** Map 4 bands → 2 internal paths */
export function mapTriageChoiceToPath(choice) {
  if (choice === 'age_expected' || choice === 'one_to_two_below') {
    return 'national_interventions';
  }
  return 'off_national_individualised';
}

export function isNC(answers) {
  const v = answers?.ncStatus;
  return v === 'age_expected' || v === 'one_to_two_below';
}
export function isBelowNC(answers) {
  const v = answers?.ncStatus;
  return v === 'two_to_three_below' || v === 'four_or_more_below';
}

export const QUESTIONNAIRE_SECTIONS = [
  {
    id: 'triage',
    title: 'Current Attainment',
    icon: '📍',
    questions: [
      {
        id: 'ncStatus',
        question: 'What is this pupil\'s current attainment level?',
        options: [
          {
            value: 'age_expected',
            label: 'The child is working at age-expected levels',
            strategies: []
          },
          {
            value: 'one_to_two_below',
            label: 'The child is working 1–2 years below',
            strategies: []
          },
          {
            value: 'two_to_three_below',
            label: 'The child is working 2–3 years below',
            strategies: []
          },
          {
            value: 'four_or_more_below',
            label: 'The child is working 4+ years below',
            strategies: []
          }
        ]
      }
    ]
  },
  {
    id: 'communication',
    title: 'Communication & Interaction',
    icon: '🗣',
    questions: [
      {
        id: 'comm_primary',
        question: 'How do they usually get their message across?',
        options: [
          {
            value: 'full_sentences',
            label: 'Full sentences for their age',
            strategies: ['universal']
          },
          {
            value: 'behind_expectations',
            label: 'Sentences but younger than age by 1–2 years',
            strategies: ['symbols', 'visuals', 'simplified_language_modelling']
          },
          {
            value: 'short_phrases',
            label: 'Short phrases / a few familiar words',
            strategies: ['symbols', 'makaton_signs', 'communication_boards']
          },
          {
            value: 'minimal_words',
            label: '5 or fewer words; mainly gestures/leading adult',
            strategies: ['makaton', 'symbols', 'intensive_interaction']
          },
          {
            value: 'non_verbal',
            label: 'Non-verbal; does not initiate communication',
            strategies: ['intensive_interaction', 'cause_effect_play', 'early_aac']
          },
          {
            value: 'echolalia',
            label: 'Repeats words/phrases but not meaningfully (echolalia)',
            strategies: ['symbols', 'makaton_signs', 'communication_modelling']
          }
        ]
      },
      {
        id: 'comm_response',
        question: 'How does the child respond to others speaking to them?',
        options: [
          {
            value: 'age_appropriate',
            label: 'Understands instructions in line with age',
            strategies: ['universal']
          },
          {
            value: 'simple_instructions',
            label: 'Understands simple instructions (1–2 steps)',
            strategies: ['visual_supports', 'clear_routines', 'key_word_signing']
          },
          {
            value: 'single_steps',
            label: 'Needs instructions broken down to single steps',
            strategies: ['now_next_boards', 'visuals', 'repetition']
          },
          {
            value: 'no_verbal_response',
            label: 'Does not respond to verbal instructions',
            strategies: ['intensive_interaction', 'visual_schedules', 'gesture_sign_modelling']
          },
          {
            value: 'inconsistent_response',
            label: 'Responds inconsistently or only when interested',
            strategies: ['visual_supports', 'sensory_breaks', 'key_word_signing']
          }
        ]
      },
      {
        id: 'comm_receptive',
        question: 'How well do they follow instructions?',
        options: [
          {
            value: 'multi_step',
            label: 'Most classroom instructions (multi-step)',
            strategies: ['universal']
          },
          {
            value: 'one_to_two_step',
            label: 'Simple 1–2 step instructions with reminders',
            strategies: ['visual_supports', 'clear_routines']
          },
          {
            value: 'single_step',
            label: 'One short step at a time',
            strategies: ['now_next_boards', 'visuals', 'repetition']
          },
          {
            value: 'visuals_required',
            label: 'Only with visuals/gestures',
            strategies: ['visual_schedules', 'gesture_sign_modelling']
          },
          {
            value: 'minimal_response',
            label: 'Rarely responds, even with visuals',
            strategies: ['intensive_interaction', 'visual_schedules']
          }
        ]
      },
      {
        id: 'comm_social',
        question: 'How does the child interact socially?',
        options: [
          {
            value: 'group_play',
            label: 'Joins group play and conversation age appropriately',
            strategies: ['universal']
          },
          {
            value: 'adults_only',
            label: 'Engages with adults but not peers',
            strategies: ['social_skills_games', 'structured_peer_play']
          },
          {
            value: 'parallel_play',
            label: 'Plays alongside peers but not with them (parallel play)',
            strategies: ['social_skills_games', 'structured_peer_play']
          },
          {
            value: 'needs_met_only',
            label: 'Only interacts to get needs met',
            strategies: ['intensive_interaction', 'social_skills_games']
          },
          {
            value: 'avoids_interaction',
            label: 'Avoids or is distressed by interaction',
            strategies: ['intensive_interaction', 'emotion_coaching']
          }
        ]
      }
    ]
  },
  {
    id: 'cognition',
    title: 'Cognition & Learning',
    icon: '🧠',
    questions: [
      {
        id: 'cog_learning',
        question: 'Which describes how they learn new things?',
        options: [
          {
            value: 'typical',
            label: 'Learns like most in the class',
            strategies: ['universal']
          },
          {
            value: 'needs_modelling',
            label: 'Learns with modelling and practice',
            strategies: ['repetition_routine', 'scaffolded_tasks']
          },
          {
            value: 'high_repetition',
            label: 'Needs lots of repetition and hands-on activities',
            strategies: ['repetition_routine', 'scaffolded_tasks', 'visuals']
          },
          {
            value: 'concrete_only',
            label: 'Needs very simple, concrete activities; struggles to generalise',
            strategies: ['early_literacy_maths', 'cause_effect', 'repetition_routine']
          },
          {
            value: 'brief_engagement',
            label: 'Engages only briefly with adult-led learning',
            strategies: ['early_literacy_maths', 'cause_effect', 'scaffolded_tasks']
          }
        ]
      },
      {
        id: 'cog_demonstrate',
        question: 'How does the child demonstrate their understanding?',
        options: [
          {
            value: 'verbal_written',
            label: 'Verbal responses or written work in line with peers',
            strategies: ['universal']
          },
          {
            value: 'limited_verbal',
            label: 'Verbal but limited or short answers',
            strategies: ['visual_choice_boards', 'multi_modal_instruction']
          },
          {
            value: 'gestures_words',
            label: 'Gestures or single words',
            strategies: ['aac_symbols', 'visual_choice_boards']
          },
          {
            value: 'points_objects',
            label: 'Points to objects/pictures',
            strategies: ['aac_symbols', 'intensive_interaction']
          },
          {
            value: 'limited_response',
            label: 'Limited or no response',
            strategies: ['intensive_interaction', 'visual_choice_boards']
          }
        ]
      },
      {
        id: 'cog_structured_tasks',
        question: 'How does the child engage with structured tasks?',
        options: [
          {
            value: 'independent',
            label: 'Works independently for expected time',
            strategies: ['universal']
          },
          {
            value: 'needs_prompts',
            label: 'Needs prompts to stay on task',
            strategies: ['chunking_tasks', 'clear_routines']
          },
          {
            value: 'brief_engagement',
            label: 'Engages briefly then disengages',
            strategies: ['free_flow', 'chunking_tasks']
          },
          {
            value: 'refuses_tasks',
            label: 'Refuses or avoids structured tasks',
            strategies: ['free_flow', 'clear_routines', 'social_stories']
          },
          {
            value: 'distressed_tasks',
            label: 'Becomes distressed by structured activities',
            strategies: ['free_flow', 'sensory_regulation_breaks', 'social_stories']
          }
        ]
      }
    ]
  },
  {
    id: 'semh',
    title: 'Social, Emotional & Mental Health (SEMH)',
    icon: '❤️',
    questions: [
      {
        id: 'semh_emotional',
        question: 'How does the child usually present emotionally in the classroom?',
        options: [
          {
            value: 'calm_regulated',
            label: 'Calm and regulated',
            strategies: ['universal']
          },
          {
            value: 'occasionally_anxious',
            label: 'Occasionally anxious or unsettled',
            strategies: ['emotional_check_ins']
          },
          {
            value: 'regularly_anxious',
            label: 'Regularly anxious, needs reassurance',
            strategies: ['zones_of_regulation', 'emotional_check_ins']
          },
          {
            value: 'easily_distressed',
            label: 'Easily distressed or overwhelmed',
            strategies: ['zones_of_regulation', 'sensory_regulation_breaks', 'co_regulation_strategies']
          },
          {
            value: 'frequent_outbursts',
            label: 'Frequent emotional outbursts or shutdowns',
            strategies: ['zones_of_regulation', 'sensory_regulation_breaks', 'co_regulation_strategies']
          }
        ]
      },
      {
        id: 'semh_transitions',
        question: 'How does the child respond to transitions or change?',
        options: [
          {
            value: 'manages_independently',
            label: 'Manages transitions independently',
            strategies: ['universal']
          },
          {
            value: 'verbal_reminders',
            label: 'Needs verbal reminders',
            strategies: ['visual_schedules']
          },
          {
            value: 'visual_support',
            label: 'Needs visual support (Now & Next)',
            strategies: ['visual_schedules', 'now_next_boards']
          },
          {
            value: 'adult_support',
            label: 'Needs adult support for most transitions',
            strategies: ['visual_schedules', 'transition_plans']
          },
          {
            value: 'distressing_transitions',
            label: 'Finds transitions very distressing',
            strategies: ['visual_schedules', 'social_stories', 'transition_plans', 'sensory_breaks_transitions']
          }
        ]
      },
      {
        id: 'semh_groups',
        question: 'How does the child respond to group settings?',
        options: [
          {
            value: 'participates_confidently',
            label: 'Participates confidently',
            strategies: ['universal']
          },
          {
            value: 'participates_with_support',
            label: 'Participates with adult support',
            strategies: ['small_group_work']
          },
          {
            value: 'prefers_small_groups',
            label: 'Prefers small groups',
            strategies: ['small_group_work']
          },
          {
            value: 'avoids_groups',
            label: 'Avoids group activities',
            strategies: ['small_group_work', 'intensive_interaction']
          },
          {
            value: 'dysregulated_groups',
            label: 'Becomes dysregulated in groups',
            strategies: ['small_group_work', 'sensory_regulation', 'emotion_coaching']
          }
        ]
      },
      {
        id: 'semh_regulation',
        question: 'When upset, how do they manage big feelings?',
        options: [
          {
            value: 'uses_taught_strategies',
            label: 'Can use taught strategies when prompted (e.g., breathing, asking for help)',
            strategies: ['universal']
          },
          {
            value: 'needs_adult_suggestion',
            label: 'Needs adult to suggest a strategy; sometimes uses it',
            strategies: ['emotional_check_ins', 'co_regulation_strategies']
          },
          {
            value: 'needs_co_regulation',
            label: 'Needs adult to co-regulate; cannot use a strategy independently',
            strategies: ['co_regulation_strategies', 'sensory_regulation_breaks']
          },
          {
            value: 'escalates_quickly',
            label: 'Escalates quickly; needs low-arousal support and space',
            strategies: ['low_arousal_response', 'sensory_regulation_breaks', 'calm_corners']
          },
          {
            value: 'shuts_down',
            label: 'Shuts down or is non-responsive when distressed',
            strategies: ['low_arousal_response', 'sensory_regulation_breaks', 'calm_corners']
          }
        ]
      }
    ]
  },
  {
    id: 'sensory',
    title: 'Sensory & Physical',
    icon: '🪴',
    questions: [
      {
        id: 'sensory_input',
        question: 'How does the child respond to sensory input (noise, touch, light, etc.)?',
        options: [
          {
            value: 'tolerates_typical',
            label: 'Tolerates typical sensory environment',
            strategies: ['universal']
          },
          {
            value: 'mild_sensitivities',
            label: 'Mild sensitivities (e.g. dislikes loud noise)',
            strategies: ['sensory_regulation_breaks']
          },
          {
            value: 'strong_reactions',
            label: 'Strong reactions to certain sensory input',
            strategies: ['sensory_regulation_breaks', 'calm_corners']
          },
          {
            value: 'seeks_sensory',
            label: 'Seeks sensory input actively',
            strategies: ['sensory_regulation_breaks', 'movement_breaks', 'sensory_diets']
          },
          {
            value: 'over_under_reactive',
            label: 'Over- or under-reactive to most sensory experiences',
            strategies: ['sensory_regulation_breaks', 'movement_breaks', 'calm_corners', 'sensory_diets']
          }
        ]
      },
      {
        id: 'fine_motor',
        question: 'How are their fine motor skills (e.g., holding tools, feeding, dressing)?',
        options: [
          {
            value: 'age_appropriate',
            label: 'Age appropriate',
            strategies: ['universal']
          },
          {
            value: 'slight_delay',
            label: 'Slight delay, manages with support',
            strategies: ['grip_strengthening_activities']
          },
          {
            value: 'noticeable_difficulty',
            label: 'Noticeable difficulty with tools (e.g. scissors, pencils)',
            strategies: ['grip_strengthening_activities', 'fine_motor_stations']
          },
          {
            value: 'avoids_fine_motor',
            label: 'Avoids fine motor tasks',
            strategies: ['grip_strengthening_activities', 'fine_motor_stations', 'free_flow_motor']
          },
          {
            value: 'hand_over_hand',
            label: 'Needs adult hand-over-hand support',
            strategies: ['grip_strengthening_activities', 'fine_motor_stations', 'free_flow_motor']
          }
        ]
      },
      {
        id: 'gross_motor',
        question: 'How are their gross motor skills (e.g., balance, coordination)?',
        options: [
          {
            value: 'typical_age',
            label: 'Typical for age',
            strategies: ['universal']
          },
          {
            value: 'slightly_delayed',
            label: 'Slightly delayed but functional',
            strategies: ['movement_breaks']
          },
          {
            value: 'delayed_unsteady',
            label: 'Delayed, unsteady or cautious',
            strategies: ['outside_time', 'movement_breaks']
          },
          {
            value: 'avoids_physical',
            label: 'Avoids physical activity',
            strategies: ['outside_time', 'movement_breaks', 'occupational_therapy_links']
          },
          {
            value: 'significant_needs',
            label: 'Has significant physical needs',
            strategies: ['outside_time', 'movement_breaks', 'occupational_therapy_links', 'adapted_equipment']
          }
        ]
      }
    ]
  }
];

/**
 * Strategy mapping for teaching plan generation
 * Maps questionnaire responses to specific strategies and activities
 */
export const STRATEGY_MAPPING = {
  // Communication strategies
  'symbols': { category: 'ongoing', activity: 'symbols-session' },
  'visuals': { category: 'ongoing', activity: 'visual-supports' },
  'simplified_language_modelling': { category: 'ongoing', activity: 'language-time' },
  'makaton_signs': { category: 'ongoing', activity: 'makaton-session' },
  'communication_boards': { category: 'ongoing', activity: 'core-board-session' },
  'makaton': { category: 'ongoing', activity: 'makaton-session' },
  'intensive_interaction': { category: 'structured', activity: 'intensive-interaction' },
  'cause_effect_play': { category: 'structured', activity: 'cause-and-effect' },
  'early_aac': { category: 'structured', activity: 'aac-session' },
  'communication_modelling': { category: 'ongoing', activity: 'language-time' },
  'visual_supports': { category: 'ongoing', activity: 'visual-supports' },
  'clear_routines': { category: 'ongoing', activity: 'visual-schedules' },
  'key_word_signing': { category: 'ongoing', activity: 'makaton-session' },
  'now_next_boards': { category: 'ongoing', activity: 'now-and-next-boards' },
  'repetition': { category: 'ongoing', activity: 'repetition-routine' },
  'visual_schedules': { category: 'ongoing', activity: 'visual-schedules' },
  'gesture_sign_modelling': { category: 'ongoing', activity: 'makaton-session' },
  'sensory_breaks': { category: 'ongoing', activity: 'sensory-regulation-breaks' },
  'social_skills_games': { category: 'structured', activity: 'social-skills-games' },
  'structured_peer_play': { category: 'structured', activity: 'social-skills' },
  'emotion_coaching': { category: 'ongoing', activity: 'emotional-check-ins' },

  // Cognition strategies
  'repetition_routine': { category: 'ongoing', activity: 'repetition-routine' },
  'scaffolded_tasks': { category: 'ongoing', activity: 'scaffolded-learning' },
  'early_literacy_maths': { category: 'structured', activity: 'early-literacy' },
  'cause_effect': { category: 'structured', activity: 'cause-and-effect' },
  'aac_symbols': { category: 'ongoing', activity: 'symbols-session' },
  'visual_choice_boards': { category: 'ongoing', activity: 'choice-boards' },
  'multi_modal_instruction': { category: 'ongoing', activity: 'multi-modal-learning' },
  'chunking_tasks': { category: 'ongoing', activity: 'task-chunking' },
  'free_flow': { category: 'structured', activity: 'free-flow-continuous-provision' },
  'social_stories': { category: 'structured', activity: 'social-stories' },

  // SEMH strategies
  'emotional_check_ins': { category: 'ongoing', activity: 'emotional-check-ins' },
  'zones_of_regulation': { category: 'ongoing', activity: 'zones-of-regulation' },
  'sensory_regulation_breaks': { category: 'ongoing', activity: 'sensory-regulation-breaks' },
  'co_regulation_strategies': { category: 'ongoing', activity: 'co-regulation' },
  'transition_plans': { category: 'ongoing', activity: 'transition-planning' },
  'sensory_breaks_transitions': { category: 'ongoing', activity: 'sensory-regulation-breaks' },
  'small_group_work': { category: 'ongoing', activity: 'small-group-learning' },
  'sensory_regulation': { category: 'ongoing', activity: 'sensory-regulation-breaks' },
  'low_arousal_response': { category: 'ongoing', activity: 'low-arousal-response' },
  'calm_corners': { category: 'ongoing', activity: 'calm-corners' },

  // Sensory & Physical strategies
  'movement_breaks': { category: 'ongoing', activity: 'movement-breaks' },
  'sensory_diets': { category: 'ongoing', activity: 'sensory-diets' },
  'grip_strengthening_activities': { category: 'structured', activity: 'grip-strengthening' },
  'fine_motor_stations': { category: 'structured', activity: 'fine-motor' },
  'free_flow_motor': { category: 'structured', activity: 'free-flow-continuous-provision' },
  'outside_time': { category: 'structured', activity: 'outside-time' },
  'occupational_therapy_links': { category: 'ongoing', activity: 'ot-referrals' },
  'adapted_equipment': { category: 'ongoing', activity: 'adapted-equipment' },

  // Universal strategies (no specific mapping needed)
  'universal': { category: 'ongoing', activity: 'universal-strategies' }
};

/**
 * Convert questionnaire responses to strategy recommendations
 * @param {Object} responses - Object with question IDs as keys and selected option values
 * @returns {Object} - Object with ongoing and structured strategies
 */
export function responsesToStrategies(responses) {
  // Import SEMH selection logic dynamically to avoid circular dependencies
  const { selectSEMHStrategies } = require('./selectSEMHStrategies.js');
  
  const allStrategies = new Set();
  
  // Collect strategies from communication, cognition, and sensory sections
  QUESTIONNAIRE_SECTIONS.forEach(section => {
    // Skip SEMH section - handled by selectSEMHStrategies
    if (section.id === 'semh') return;
    
    section.questions.forEach(question => {
      const response = responses[question.id];
      if (response) {
        const option = question.options.find(opt => opt.value === response);
        if (option) {
          option.strategies.forEach(strategy => allStrategies.add(strategy));
        }
      }
    });
  });

  // Get SEMH strategies using ability-aware selection
  const semhStrategies = selectSEMHStrategies(responses);
  
  // Categorize non-SEMH strategies
  const ongoing = [];
  const structured = [];
  
  allStrategies.forEach(strategy => {
    const mapping = STRATEGY_MAPPING[strategy];
    if (mapping) {
      if (mapping.category === 'ongoing') {
        ongoing.push(mapping.activity);
      } else if (mapping.category === 'structured') {
        structured.push(mapping.activity);
      }
    }
  });
  
  // Add SEMH strategies
  ongoing.push(...semhStrategies.ongoing);
  structured.push(...semhStrategies.structured);

  // Remove duplicates
  return {
    ongoing: [...new Set(ongoing)],
    structured: [...new Set(structured)]
  };
}

/**
 * Generate rationale based on questionnaire responses
 * @param {Object} responses - Object with question IDs as keys and selected option values
 * @returns {string} - Rationale text
 */
export function generateQuestionnaireRationale(responses) {
  const rationale = [];
  
  // Analyze communication needs
  const commPrimary = responses.comm_primary;
  if (commPrimary && commPrimary !== 'full_sentences') {
    rationale.push('Communication support strategies recommended based on observed communication patterns');
  }
  
  // Analyze learning needs
  const cogLearning = responses.cog_learning;
  if (cogLearning && ['struggles_retain', 'little_engagement'].includes(cogLearning)) {
    rationale.push('Enhanced learning support strategies for engagement and retention');
  }
  
  // Analyze SEMH needs
  const semhEmotional = responses.semh_emotional;
  if (semhEmotional && ['easily_distressed', 'frequent_outbursts'].includes(semhEmotional)) {
    rationale.push('Emotional regulation and sensory support strategies prioritized');
  }
  
  // Analyze sensory needs
  const sensoryInput = responses.sensory_input;
  if (sensoryInput && ['strong_reactions', 'over_under_reactive'].includes(sensoryInput)) {
    rationale.push('Comprehensive sensory regulation approach recommended');
  }
  
  if (rationale.length === 0) {
    return 'Universal strategies and age-appropriate activities recommended based on questionnaire responses';
  }
  
  return rationale.join('. ') + '.';
}
